/**
 * SAP holiday-calendar + work-schedule landing service.
 *
 * Reads/writes the sap_holiday_calendar + sap_work_schedules tables (migration
 * 00425) — the landing spot for a SAP pull of the customer's own holiday
 * calendar and work-schedule rules. Mirrors chargeObjectService: defensive
 * empty-on-error, live pull gated behind the SAP backend (IS_LIVE_SAP), and it
 * NEVER writes fabricated SAP data (the demo tables stay empty).
 *
 * Pure types + the merge seam live in lib/sapCalendar.ts (unit-tested there).
 * The consuming side already exists (lib/holidays.ts federal calendar,
 * users.schedule_type patterns, working_day_calendars); wiring the overlay into
 * the timesheet's standard-day logic is a later slice.
 */
import { supabase } from '@/lib/supabase'
import { IS_LIVE_SAP } from '@/config/sapBackend'
import { setSapHolidayOverlay } from '../lib/holidays'
import { sapHolidayToHoliday } from '../lib/sapCalendar'
import type { SapHoliday, SapWorkSchedule, CalendarSource } from '../lib/sapCalendar'

export type { SapHoliday, SapWorkSchedule, CalendarSource } from '../lib/sapCalendar'
export {
  sapScheduleToPattern, sapHolidayToHoliday, mergeHolidaysWithFederal,
} from '../lib/sapCalendar'

export interface PullResult {
  ok: boolean
  imported: number
  message: string
}

// ── Row <-> app mapping ──────────────────────────────────────────────────────

type HolidayRow = {
  org_id: string; id: string; calendar_id: string; holiday_date: string
  name: string | null; holiday_class: string | null; is_workday: boolean
  active: boolean; source: string
}
type ScheduleRow = {
  org_id: string; id: string; schedule_rule: string; name: string | null
  weekly_hours: number | null; pattern: string | null
  daily_targets: Record<string, number> | null; active: boolean; source: string
}

function asSource(s: string): CalendarSource { return s === 'manual' ? 'manual' : 'sap_import' }

function holidayToApp(r: HolidayRow): SapHoliday {
  return {
    orgId: r.org_id, id: r.id, calendarId: r.calendar_id, date: r.holiday_date,
    name: r.name, holidayClass: r.holiday_class, isWorkday: !!r.is_workday,
    active: r.active, source: asSource(r.source),
  }
}
function scheduleToApp(r: ScheduleRow): SapWorkSchedule {
  return {
    orgId: r.org_id, id: r.id, scheduleRule: r.schedule_rule, name: r.name,
    weeklyHours: r.weekly_hours, pattern: r.pattern, dailyTargets: r.daily_targets,
    active: r.active, source: asSource(r.source),
  }
}

// ── Reads (defensive: table may be unapplied / empty) ────────────────────────

const HOLIDAY_COLS = 'org_id, id, calendar_id, holiday_date, name, holiday_class, is_workday, active, source'
const SCHEDULE_COLS = 'org_id, id, schedule_rule, name, weekly_hours, pattern, daily_targets, active, source'

export async function listSapHolidays(orgId: string): Promise<SapHoliday[]> {
  try {
    const { data, error } = await supabase.from('sap_holiday_calendar')
      .select(HOLIDAY_COLS).eq('org_id', orgId).order('holiday_date', { ascending: true })
    if (error) { console.warn('[sapCalendarService] listHolidays', error.message); return [] }
    return (data ?? []).map((r) => holidayToApp(r as HolidayRow))
  } catch (e) { console.warn('[sapCalendarService] listHolidays threw', e); return [] }
}

export async function listSapWorkSchedules(orgId: string): Promise<SapWorkSchedule[]> {
  try {
    const { data, error } = await supabase.from('sap_work_schedules')
      .select(SCHEDULE_COLS).eq('org_id', orgId).order('schedule_rule', { ascending: true })
    if (error) { console.warn('[sapCalendarService] listSchedules', error.message); return [] }
    return (data ?? []).map((r) => scheduleToApp(r as ScheduleRow))
  } catch (e) { console.warn('[sapCalendarService] listSchedules threw', e); return [] }
}

// ── Wipe a bad pull (admin) ──────────────────────────────────────────────────

export async function deleteAllSapHolidays(orgId: string): Promise<boolean> {
  const { error } = await supabase.from('sap_holiday_calendar').delete().eq('org_id', orgId)
  if (error) { console.warn('[sapCalendarService] deleteAllHolidays', error.message); return false }
  return true
}
export async function deleteAllSapWorkSchedules(orgId: string): Promise<boolean> {
  const { error } = await supabase.from('sap_work_schedules').delete().eq('org_id', orgId)
  if (error) { console.warn('[sapCalendarService] deleteAllSchedules', error.message); return false }
  return true
}

// ── Live pull (honest stub — transport is wired with Josh's sync) ────────────

export async function pullSapHolidays(_orgId: string, _calendarId = 'US'): Promise<PullResult> {
  if (!IS_LIVE_SAP) {
    return { ok: false, imported: 0, message: 'No live SAP backend (demo mode) — holidays come from the built-in federal calendar.' }
  }
  // Live SAP: the holiday-calendar OData read is wired in a later pass with the
  // SAP sync. Until then, report not-connected — we never write fabricated data.
  return { ok: false, imported: 0, message: 'Live SAP holiday-calendar connector not configured yet. Your pull will apply once the read service is wired.' }
}

export async function pullSapWorkSchedules(_orgId: string): Promise<PullResult> {
  if (!IS_LIVE_SAP) {
    return { ok: false, imported: 0, message: 'No live SAP backend (demo mode) — schedules come from users.schedule_type.' }
  }
  return { ok: false, imported: 0, message: 'Live SAP work-schedule connector not configured yet. Your pull will apply once the read service is wired.' }
}

// ── Prime the app-wide holiday overlay ───────────────────────────────────────

/**
 * Load the landed SAP holidays and install them as the app-wide holiday overlay
 * (lib/holidays), so a live-SAP fork's own calendar drives isHoliday /
 * holidaysInRange / the week-target standard-day math. No-op in the demo
 * (IS_LIVE_SAP false → federal calendar stands). Call at reference-data
 * bootstrap and after a pull. Best-effort: an empty/failed read leaves the
 * federal calendar in place.
 */
export async function primeHolidayOverlay(orgId: string): Promise<void> {
  if (!IS_LIVE_SAP) return
  const rows = await listSapHolidays(orgId)
  const holidays = rows.filter((h) => h.active && !h.isWorkday).map(sapHolidayToHoliday)
  const workdayDates = rows.filter((h) => h.active && h.isWorkday).map((h) => h.date)
  setSapHolidayOverlay(holidays, workdayDates)
}
